import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, X, Database, Upload, FileText, Users, Target, CheckCircle, AlertCircle, FileSpreadsheet, Trash2, Download, Loader2, ExternalLink } from 'lucide-react'
import { toast as sonnerToast } from 'sonner'
import { toast as notifyToast } from '@/hooks/use-toast'

// Unified toast that shows sonner popup AND captures to bell notification
const toast = {
  success: (message: string) => {
    sonnerToast.success(message)
    notifyToast({ title: 'Success', description: message, variant: 'default' as any })
  },
  warning: (message: string) => {
    sonnerToast.warning(message)
    notifyToast({ title: 'Warning', description: message, variant: 'default' as any })
  },
  error: (message: string) => {
    sonnerToast.error(message)
    notifyToast({ title: 'Error', description: message, variant: 'destructive' })
  },
}
import { supabase } from '@/integrations/supabase/client'
import type { ClientTAMForm } from '@/types/client'
import type { Client } from '@/hooks/useClients'
import { TAMUploadHistory } from '../tam/TAMUploadHistory'
import { AccountUploadDialog } from '@/components/master-crm/AccountUploadDialog'
import { ContactUploadDialog } from '@/components/master-crm/ContactUploadDialog'
import { useDashboard } from '@/contexts/DashboardContext'

interface ClientTAMStepProps {
  data: ClientTAMForm
  onChange: (data: Partial<ClientTAMForm>) => void
  isLoading?: boolean
  client?: Client | null
}

interface UploadedFile {
  id: string
  name: string
  type: 'tam_accounts' | 'blocklist' | 'tam_contact_list'
  size: number
  uploadDate: string
  recordCount: number
  columns: string[]
  status: 'uploaded' | 'processing' | 'imported' | 'error'
  batchId?: string
  errorMessage?: string
  stats?: {
    created: number
    updated: number
    errors: number
    skipped: number
  }
  batchProgress?: {
    current: number
    total: number
    processedRows: number
    totalRows: number
  }
}

export function ClientTAMStep({ data, onChange, isLoading, client }: ClientTAMStepProps) {
  const navigate = useNavigate()
  const { setSelectedClient } = useDashboard()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [showAccountUploadDialog, setShowAccountUploadDialog] = useState(false)
  const [showContactUploadDialog, setShowContactUploadDialog] = useState(false)

  const navigateWithClient = (path: string) => {
    if (client?.id) setSelectedClient(client.id)
    navigate(path)
  }

  // Upload Summary stats from database
  const [summaryStats, setSummaryStats] = useState({
    tamListCount: 0,
    accountsCsv: 0,
    accountsEvent: 0,
    contactsCsv: 0,
    contactsEvent: 0,
    blocklistReply: 0,
    blocklistActive: 0,
  })

  useEffect(() => {
    if (!client?.id) return

    const fetchSummaryStats = async () => {
      try {
        const smartleadClientId = (client as any).client_id
        const { data, error } = await supabase.rpc('get_client_tam_summary', {
          p_client_id: client.id,
          p_smartlead_client_id: smartleadClientId ? String(smartleadClientId) : null,
        })

        if (error) {
          console.error('Error fetching summary stats:', error.message)
          return
        }

        if (data) {
          setSummaryStats({
            tamListCount: data.tam_list_count || 0,
            accountsCsv: data.accounts_csv || 0,
            accountsEvent: data.accounts_event || 0,
            contactsCsv: data.contacts_csv || 0,
            contactsEvent: data.contacts_event || 0,
            blocklistReply: data.blocklist_total || 0,
            blocklistActive: data.blocklist_active || 0,
          })
        }
      } catch (err) {
        console.error('Error fetching summary stats:', err)
      }
    }

    fetchSummaryStats()
  }, [client?.id])

  // Template data - matching actual CSV templates
  const tamTemplateData = [
    ['Domain', 'Linkedin', 'Linkedin ID', 'Company Name', 'Internal ID', 'Hubspot ID (OB)'],
    ['acme.com', 'https://linkedin.com/company/acme-corp', 'acme-corp-id', 'Acme Corp', 'INT-001', 'HS-001'],
    ['techstart.io', 'https://linkedin.com/company/techstart', 'techstart-id', 'TechStart Inc', 'INT-002', 'HS-002'],
    ['globalsolutions.com', 'https://linkedin.com/company/global-solutions', 'globalsolutions-id', 'Global Solutions', 'INT-003', 'HS-003']
  ]

  const tamContactListTemplateData = [
    ['Firstname', 'Lastname', 'Contact Linkedin', 'Contact Linkedin ID', 'Email', 'Phone', 'Domain', 'Company Linkedin', 'Company Linkedin ID', 'Company Name', 'Internal ID', 'Hubspot ID (OB)'],
    ['John', 'Doe', 'https://linkedin.com/in/johndoe', 'johndoe-id', 'john.doe@acme.com', '+1-555-0100', 'acme.com', 'https://linkedin.com/company/acme-corp', 'acme-corp-id', 'Acme Corp', 'INT-001', 'HS-001'],
    ['Jane', 'Smith', 'https://linkedin.com/in/janesmith', 'janesmith-id', 'jane.smith@techstart.io', '+1-555-0101', 'techstart.io', 'https://linkedin.com/company/techstart', 'techstart-id', 'TechStart Inc', 'INT-002', 'HS-002']
  ]

  const blocklistTemplateData = [
    ['Company Name', 'Domain', 'Reason', 'Date Added'],
    ['Competitor Corp', 'competitor.com', 'Direct competitor', '2024-01-15'],
    ['Former Client', 'formerclient.com', 'Previous customer - not interested', '2024-01-10'],
    ['Blacklisted Company', 'blacklisted.com', 'Spam complaints', '2024-01-05'],
    ['Do Not Contact', 'donotcontact.com', 'Explicit opt-out request', '2024-01-01']
  ]

  const downloadTemplate = (type: 'tam_accounts' | 'blocklist' | 'tam_contact_list') => {
    let data: string[][]
    let filename: string
    
    if (type === 'tam_accounts') {
      data = tamTemplateData
      filename = 'TAM List Template - Companies.csv'
    } else if (type === 'tam_contact_list') {
      data = tamContactListTemplateData
      filename = 'TAM List Template - Contacts.csv'
    } else {
      data = blocklistTemplateData
      filename = 'blocklist_template.csv'
    }
    
    // Convert to CSV string
    const csvContent = data.map(row => row.join(',')).join('\n')
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast.success(`${filename} downloaded successfully`)
  }

  // Parse a single CSV line respecting quoted fields (handles commas inside quotes)
  const parseCSVLine = (line: string): string[] => {
    const fields: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'
          i++ // skip escaped quote
        } else {
          inQuotes = !inQuotes
        }
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim())
        current = ''
      } else {
        current += ch
      }
    }
    fields.push(current.trim())
    return fields
  }

  // Parse CSV text into array of objects keyed by normalized header names
  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0)
    if (lines.length < 2) return []

    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase())
    const rows: Record<string, string>[] = []

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i])
      const row: Record<string, string> = {}
      headers.forEach((header, idx) => {
        if (values[idx]) row[header] = values[idx]
      })
      rows.push(row)
    }
    return rows
  }

  // Map a TAM Accounts CSV row to ingest-accounts AccountRecord format
  const mapAccountRow = (row: Record<string, string>) => ({
    name: row['company name'] || row['company_name'] || row['name'] || undefined,
    domain: row['domain'] || row['website'] || undefined,
    linkedin_url: row['linkedin'] || row['linkedin_url'] || row['company linkedin'] || row['company_linkedin'] || undefined,
  })

  // Map a TAM Contact List CSV row to ingest-contacts ContactRecord format
  const mapContactRow = (row: Record<string, string>) => ({
    first_name: row['firstname'] || row['first_name'] || row['first name'] || undefined,
    last_name: row['lastname'] || row['last_name'] || row['last name'] || undefined,
    email: row['email'] || row['primary_email'] || undefined,
    phone: row['phone'] || row['primary_phone'] || undefined,
    linkedin_url: row['contact linkedin'] || row['contact_linkedin'] || row['linkedin_url'] || undefined,
    company_name: row['company name'] || row['company_name'] || undefined,
    company_domain: row['domain'] || row['company domain'] || row['company_domain'] || undefined,
    company_linkedin: row['company linkedin'] || row['company_linkedin'] || undefined,
  })

  const BATCH_SIZE = 25

  // Helper to split an array into chunks
  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = []
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size))
    }
    return chunks
  }

  // Helper to update batch progress on a file
  const updateFileProgress = (fileId: string, current: number, total: number, processedRows: number, totalRows: number) => {
    setUploadedFiles(prev =>
      prev.map(f => f.id === fileId ? {
        ...f,
        batchProgress: { current, total, processedRows, totalRows },
      } : f)
    )
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'tam_accounts' | 'blocklist' | 'tam_contact_list') => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!client?.id) {
      toast.error('Client information is required to upload files')
      return
    }

    // Basic file validation
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a CSV or Excel file')
      return
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB')
      return
    }

    // Validate file is not empty
    const text = await file.text()
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0)
    if (lines.length < 2) {
      toast.error('CSV file must contain at least a header row and one data row')
      return
    }

    // Create file object for UI
    const newFile: UploadedFile = {
      id: crypto.randomUUID(),
      name: file.name,
      type,
      size: file.size,
      uploadDate: new Date().toISOString(),
      recordCount: lines.length - 1,
      columns: lines[0].split(',').map(h => h.trim()),
      status: 'uploaded'
    }

    setUploadedFiles(prev => [...prev, newFile])
    setIsUploading(true)

    // Update status to processing
    setUploadedFiles(prev =>
      prev.map(f => f.id === newFile.id ? { ...f, status: 'processing' } : f)
    )

    try {
      const parsedRows = parseCSV(text)
      const tamListName = file.name.replace(/\.(csv|xlsx|xls)$/i, '')

      if (type === 'tam_accounts') {
        // Batch accounts in chunks of 200 to prevent edge function timeouts
        // (500 was too large when many new companies need creating)
        const accounts = parsedRows.map(mapAccountRow)
        const ACC_BATCH_SIZE = 200
        let accCreated = 0, accMatched = 0, accErrors = 0
        const totalBatches = Math.ceil(accounts.length / ACC_BATCH_SIZE)

        for (let batchIdx = 0; batchIdx < accounts.length; batchIdx += ACC_BATCH_SIZE) {
          const batch = accounts.slice(batchIdx, batchIdx + ACC_BATCH_SIZE)
          const currentBatch = Math.floor(batchIdx / ACC_BATCH_SIZE) + 1

          // Update progress BEFORE the request
          updateFileProgress(newFile.id, currentBatch, totalBatches, batchIdx, accounts.length)

          const { data: batchResult, error: batchError } = await supabase.functions.invoke('ingest-accounts-bulk', {
            body: {
              client_id: client.id,
              tam_list_name: tamListName,
              accounts: batch,
            },
          })

          if (batchError) {
            console.error(`[TAM Upload] Batch ${currentBatch}/${totalBatches} error:`, batchError.message)
            accErrors += batch.length
          } else if (batchResult) {
            accCreated += batchResult.created || 0
            accMatched += batchResult.matched || 0
            accErrors += batchResult.errors || 0
            console.log(`[TAM Upload] Batch ${currentBatch}/${totalBatches}: ${batchResult.created || 0} created, ${batchResult.matched || 0} matched, ${batchResult.errors || 0} errors`)
          }

          // Update progress AFTER the request
          updateFileProgress(newFile.id, currentBatch, totalBatches, batchIdx + batch.length, accounts.length)
        }

        // Final state
        setUploadedFiles(prev =>
          prev.map(f => f.id === newFile.id ? {
            ...f,
            status: accErrors > 0 && accCreated === 0 && accMatched === 0 ? 'error' : 'imported',
            batchProgress: undefined,
            stats: { created: accCreated, updated: accMatched, errors: accErrors, skipped: 0 },
          } : f)
        )

        if (accErrors === 0) {
          toast.success(`${file.name} imported: ${accCreated} created, ${accMatched} matched`)
        } else if (accCreated > 0 || accMatched > 0) {
          toast.warning(`${file.name}: ${accCreated} created, ${accMatched} matched, ${accErrors} errors`)
        } else {
          toast.error(`${file.name} failed: ${accErrors} errors`)
        }

      } else if (type === 'tam_contact_list') {
        const allContacts = parsedRows.map(mapContactRow)
        const batches = chunk(allContacts, BATCH_SIZE)
        let totalCreated = 0, totalMatched = 0, totalErrors = 0

        for (let i = 0; i < batches.length; i++) {
          updateFileProgress(newFile.id, i + 1, batches.length, i * BATCH_SIZE, allContacts.length)

          try {
            const { data: result, error } = await supabase.functions.invoke('ingest-contacts', {
              body: {
                client_id: client.id,
                contacts: batches[i],
              },
            })

            if (error || !result) {
              console.error(`Batch ${i + 1} failed:`, error)
              totalErrors += batches[i].length
            } else {
              totalCreated += result.created || 0
              totalMatched += result.matched || 0
              totalErrors += result.errors || 0
            }
          } catch (batchErr) {
            console.error(`Batch ${i + 1} exception:`, batchErr)
            totalErrors += batches[i].length
          }

          setUploadedFiles(prev =>
            prev.map(f => f.id === newFile.id ? {
              ...f,
              stats: { created: totalCreated, updated: totalMatched, errors: totalErrors, skipped: 0 },
            } : f)
          )
        }

        setUploadedFiles(prev =>
          prev.map(f => f.id === newFile.id ? {
            ...f,
            status: totalErrors > 0 && totalCreated === 0 && totalMatched === 0 ? 'error' : 'imported',
            batchProgress: undefined,
            stats: { created: totalCreated, updated: totalMatched, errors: totalErrors, skipped: 0 },
          } : f)
        )

        if (totalErrors === 0) {
          toast.success(`${file.name} imported successfully: ${totalCreated} contacts created, ${totalMatched} matched`)
        } else if (totalCreated > 0 || totalMatched > 0) {
          toast.warning(`${file.name} imported with errors: ${totalCreated} created, ${totalMatched} matched, ${totalErrors} errors`)
        } else {
          toast.error(`${file.name} failed to import: ${totalErrors} errors`)
        }

      } else {
        // Blocklist: batch through ingest-accounts then mark as blocked
        const rows = parsedRows
        const blocklistAccounts = rows.map(row => ({
          name: row['company name'] || row['company_name'] || row['name'] || '',
          domain: row['domain'] || row['website'] || '',
          reason: row['reason'] || '',
        })).filter(r => r.domain || r.name)

        const batches = chunk(blocklistAccounts, BATCH_SIZE)
        let totalCreated = 0, totalErrors = 0

        for (let i = 0; i < batches.length; i++) {
          updateFileProgress(newFile.id, i + 1, batches.length, i * BATCH_SIZE, blocklistAccounts.length)

          const batch = batches[i]
          const { data: result, error } = await supabase.functions.invoke('ingest-accounts', {
            body: {
              client_id: client.id,
              accounts: batch.map(r => ({ name: r.name, domain: r.domain })),
            },
          })

          if (error || !result?.results) { totalErrors += batch.length; continue }

          // Mark each resolved company as blocked
          for (let j = 0; j < result.results.length; j++) {
            const companyId = result.results[j]?.company_id
            if (!companyId) { totalErrors++; continue }

            const reason = batch[j]?.reason || 'Blocklisted via CSV upload'
            await supabase
              .from('tam_memberships')
              .update({ status: 'blocked', notes: reason })
              .eq('company_id', companyId)
              .eq('client_id', client.id)

            totalCreated++
          }

          setUploadedFiles(prev =>
            prev.map(f => f.id === newFile.id ? {
              ...f,
              stats: { created: totalCreated, updated: 0, errors: totalErrors, skipped: 0 },
            } : f)
          )
        }

        setUploadedFiles(prev =>
          prev.map(f => f.id === newFile.id ? {
            ...f,
            status: totalErrors > 0 && totalCreated === 0 ? 'error' : 'imported',
            batchProgress: undefined,
            stats: { created: totalCreated, updated: 0, errors: totalErrors, skipped: 0 },
          } : f)
        )

        if (totalErrors === 0) {
          toast.success(`${file.name} blocklist imported: ${totalCreated} companies blocked`)
        } else if (totalCreated > 0) {
          toast.warning(`${file.name} blocklist imported with errors: ${totalCreated} blocked, ${totalErrors} errors`)
        } else {
          toast.error(`${file.name} blocklist failed: ${totalErrors} errors`)
        }
      }

    } catch (error) {
      console.error('Error uploading file:', error)
      setUploadedFiles(prev =>
        prev.map(f => f.id === newFile.id ? {
          ...f,
          status: 'error',
          batchProgress: undefined,
          errorMessage: error instanceof Error ? error.message : 'Unknown error'
        } : f)
      )
      toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsUploading(false)
      // Clear the input
      event.target.value = ''
    }
  }

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
    toast.success('File removed')
  }

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploaded':
        return <FileSpreadsheet className="h-4 w-4 text-blue-500" />
      case 'processing':
        return <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      case 'imported':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />
    }
  }

  const getStatusText = (file: UploadedFile) => {
    if (file.status === 'processing' && file.batchProgress) {
      const { current, total, processedRows, totalRows } = file.batchProgress
      return `Batch ${current}/${total} (${processedRows.toLocaleString()}/${totalRows.toLocaleString()} rows)`
    }
    switch (file.status) {
      case 'uploaded':
        return 'Uploaded'
      case 'processing':
        return 'Processing...'
      case 'imported':
        return 'Imported to CRM'
      case 'error':
        return 'Import Failed'
    }
  }

  const tamFiles = uploadedFiles.filter(f => f.type === 'tam_accounts')
  const tamContactListFiles = uploadedFiles.filter(f => f.type === 'tam_contact_list')
  const blocklistFiles = uploadedFiles.filter(f => f.type === 'blocklist')

  return (
    <div className="space-y-6">
      {/* Upload Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-primary" />
            <CardTitle>Upload Summary</CardTitle>
          </div>
          <CardDescription>
            Overview of uploaded data and CRM integration status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {/* TAM Lists */}
            <div
              className="text-center p-4 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors group relative"
              onClick={() => navigateWithClient('/master-crm/tam')}
            >
              <ExternalLink className="w-3 h-3 absolute top-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="text-2xl font-bold text-primary">
                {summaryStats.tamListCount}
              </div>
              <div className="text-sm text-muted-foreground">TAM Lists</div>
              <div className="text-xs text-muted-foreground mt-1">
                Target account lists
              </div>
            </div>

            {/* Accounts */}
            <div
              className="text-center p-4 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors group relative"
              onClick={() => navigateWithClient('/master-crm/accounts')}
            >
              <ExternalLink className="w-3 h-3 absolute top-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="text-2xl font-bold text-primary">
                {(summaryStats.accountsCsv + summaryStats.accountsEvent).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Accounts</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>{summaryStats.accountsCsv.toLocaleString()} via CSV</div>
                <div>{summaryStats.accountsEvent.toLocaleString()} via Events</div>
              </div>
            </div>

            {/* Contacts */}
            <div
              className="text-center p-4 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors group relative"
              onClick={() => navigateWithClient('/master-crm/contacts')}
            >
              <ExternalLink className="w-3 h-3 absolute top-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="text-2xl font-bold text-primary">
                {(summaryStats.contactsCsv + summaryStats.contactsEvent).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Contacts</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>{summaryStats.contactsCsv.toLocaleString()} via CSV</div>
                <div>{summaryStats.contactsEvent.toLocaleString()} via Events</div>
              </div>
            </div>

            {/* Blocklist */}
            <div
              className="text-center p-4 bg-muted/20 rounded-lg cursor-pointer hover:bg-muted/40 transition-colors group relative"
              onClick={() => navigateWithClient('/master-inbox')}
            >
              <ExternalLink className="w-3 h-3 absolute top-2 right-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="text-2xl font-bold text-primary">
                {summaryStats.blocklistActive.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Blocklist</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>{summaryStats.blocklistActive.toLocaleString()} active blocks</div>
                <div>{summaryStats.blocklistReply.toLocaleString()} total entries</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unified File Upload */}
      <Card>
        <CardHeader>
          <CardTitle>File Uploads</CardTitle>
          <CardDescription>
            Upload TAM accounts, contact lists, or blocklists
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload Type Selector */}
          <div className="space-y-2">
            <Label htmlFor="upload-type">Upload Type</Label>
            <Select
              value={data.upload_type || 'tam_accounts'}
              onValueChange={(value) => onChange({ upload_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select upload type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tam_accounts">TAM Accounts</SelectItem>
                <SelectItem value="tam_contact_list">TAM Contact List</SelectItem>
                <SelectItem value="blocklist">Blocklist</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Download Template Button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate((data.upload_type || 'tam_accounts') as 'tam_accounts' | 'blocklist' | 'tam_contact_list')}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download CSV Template
            </Button>
          </div>

          {/* Upload Area */}
          {(data.upload_type || 'tam_accounts') === 'tam_accounts' ? (
            <>
              <div
                className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => setShowAccountUploadDialog(true)}
              >
                <FileSpreadsheet className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <div className="space-y-1">
                  <div className="text-sm font-medium">Upload CSV File</div>
                  <p className="text-xs text-muted-foreground">
                    Opens column mapping dialog — map your CSV columns to CRM fields
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supports CSV and Excel files (.csv, .xlsx, .xls)
                  </p>
                </div>
              </div>
              {client?.id && (
                <AccountUploadDialog
                  open={showAccountUploadDialog}
                  onOpenChange={setShowAccountUploadDialog}
                  clientId={client.id as any}
                  clientName={client.name}
                  onUploadComplete={() => {
                    setShowAccountUploadDialog(false)
                  }}
                />
              )}
            </>
          ) : data.upload_type === 'tam_contact_list' ? (
            <>
              <div
                className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => setShowContactUploadDialog(true)}
              >
                <FileSpreadsheet className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <div className="space-y-1">
                  <div className="text-sm font-medium">Upload CSV File</div>
                  <p className="text-xs text-muted-foreground">
                    Opens column mapping dialog — map your CSV columns to CRM fields
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supports CSV and Excel files (.csv, .xlsx, .xls)
                  </p>
                </div>
              </div>
              {client?.id && (
                <ContactUploadDialog
                  open={showContactUploadDialog}
                  onOpenChange={setShowContactUploadDialog}
                  clientId={client.id as any}
                  clientName={client.name}
                  onUploadComplete={() => {
                    setShowContactUploadDialog(false)
                  }}
                />
              )}
            </>
          ) : (
            <div
              className={`border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => !isUploading && fileRef.current?.click()}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-6 w-6 mx-auto mb-2 text-primary animate-spin" />
                  <div className="text-sm font-medium">Processing upload...</div>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Upload CSV File</div>
                    <input
                      ref={fileRef}
                      id="file-upload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => handleFileUpload(e, (data.upload_type || 'tam_accounts') as 'tam_accounts' | 'blocklist' | 'tam_contact_list')}
                      className="hidden"
                      disabled={isUploading}
                    />
                    <p className="text-xs text-muted-foreground">
                      Supports CSV and Excel files (.csv, .xlsx, .xls)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data.upload_type === 'tam_contact_list' ? 'Expected columns: Firstname, Lastname, Contact Linkedin, Email, Phone, Domain, Company Name' :
                       'Expected columns: Company Name, Domain, Reason, Date Added'}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Uploaded Files Display */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">Uploaded Files</Label>
              <div className="space-y-2">
                {uploadedFiles.map(file => (
                  <div key={file.id} className="p-3 border rounded-lg bg-muted/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        {getStatusIcon(file.status)}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{file.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {file.recordCount.toLocaleString()} records • {(file.size / 1024).toFixed(1)} KB • {file.type.replace('_', ' ').replace(/bw/g, l => l.toUpperCase())}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={file.status === 'imported' ? 'default' : file.status === 'error' ? 'destructive' : 'secondary'}>
                          {getStatusText(file)}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(file.id)}
                          className="h-8 w-8 p-0"
                          disabled={file.status === 'processing'}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {file.status === 'processing' && file.batchProgress && (
                      <div className="space-y-1">
                        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${file.batchProgress.totalRows > 0 ? Math.round((file.batchProgress.processedRows / file.batchProgress.totalRows) * 100) : 0}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground flex justify-between">
                          <span>{file.batchProgress.totalRows > 0 ? Math.round((file.batchProgress.processedRows / file.batchProgress.totalRows) * 100) : 0}% complete</span>
                          <span>{file.batchProgress.processedRows.toLocaleString()} / {file.batchProgress.totalRows.toLocaleString()} rows</span>
                        </div>
                      </div>
                    )}
                    {file.stats && (
                      <div className="text-xs text-muted-foreground flex gap-3">
                        <span className="text-green-600">✓ {file.stats.created} created</span>
                        {file.stats.updated > 0 && <span className="text-blue-600">↻ {file.stats.updated} matched</span>}
                        {file.stats.errors > 0 && <span className="text-red-600">✗ {file.stats.errors} errors</span>}
                      </div>
                    )}
                    {file.errorMessage && (
                      <div className="text-xs text-red-600">{file.errorMessage}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Clay Table URLs */}
      <Card>
        <CardHeader>
          <CardTitle>Clay Table URLs</CardTitle>
          <CardDescription>
            Enter the Clay table URLs for TAM tables and campaign stages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Clay TAM Tables Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <Label className="text-base font-semibold">Clay TAM Tables</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="tam-accounts-url">Account Table</Label>
                <Input
                  id="tam-accounts-url"
                  type="url"
                  placeholder="https://clay.com/table/tam-accounts"
                  value={data.tam_accounts_url || ''}
                  onChange={(e) => onChange({ tam_accounts_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="tam-contacts-url">Contact Table</Label>
                <Input
                  id="tam-contacts-url"
                  type="url"
                  placeholder="https://clay.com/table/tam-contacts"
                  value={data.tam_contacts_url || ''}
                  onChange={(e) => onChange({ tam_contacts_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border my-4"></div>

          {/* Other Clay Tables Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <Label className="text-base font-semibold">Other Clay Tables</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="meetings-scheduled-url">Meetings Scheduled</Label>
                <Input
                  id="meetings-scheduled-url"
                  type="url"
                  placeholder="https://clay.com/table/discovery-calls"
                  value={data.meetings_scheduled_url || ''}
                  onChange={(e) => onChange({ meetings_scheduled_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qualified-meetings-url">Qualified Meetings</Label>
                <Input
                  id="qualified-meetings-url"
                  type="url"
                  placeholder="https://clay.com/table/demo-calls"
                  value={data.qualified_meetings_url || ''}
                  onChange={(e) => onChange({ qualified_meetings_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="demos-requested-url">Demo's Requested</Label>
                <Input
                  id="demos-requested-url"
                  type="url"
                  placeholder="https://clay.com/table/request-demos"
                  value={data.demos_requested_url || ''}
                  onChange={(e) => onChange({ demos_requested_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="deals-won-url">Won Deals</Label>
                <Input
                  id="deals-won-url"
                  type="url"
                  placeholder="https://clay.com/table/deals-won"
                  value={data.deals_won_url || ''}
                  onChange={(e) => onChange({ deals_won_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="accounts-owned-url">Accounts owned</Label>
                <Input
                  id="accounts-owned-url"
                  type="url"
                  placeholder="https://clay.com/table/deals-owned"
                  value={data.accounts_owned_url || ''}
                  onChange={(e) => onChange({ accounts_owned_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="current-customers-url">Current customers</Label>
                <Input
                  id="current-customers-url"
                  type="url"
                  placeholder="https://clay.com/table/current-customers"
                  value={data.current_customers_url || ''}
                  onChange={(e) => onChange({ current_customers_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="customer-signups-url">Customer Sign ups</Label>
                <Input
                  id="customer-signups-url"
                  type="url"
                  placeholder="https://clay.com/table/customer-signups"
                  value={data.customer_signups_url || ''}
                  onChange={(e) => onChange({ customer_signups_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="churned-customers-url">Churned Customers</Label>
                <Input
                  id="churned-customers-url"
                  type="url"
                  placeholder="https://clay.com/table/churned-customers"
                  value={data.churned_customers_url || ''}
                  onChange={(e) => onChange({ churned_customers_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dnc-blocklist-url">DNC / Manual Blocklist</Label>
                <Input
                  id="dnc-blocklist-url"
                  type="url"
                  placeholder="https://clay.com/table/dnc-blocklist"
                  value={data.dnc_blocklist_url || ''}
                  onChange={(e) => onChange({ dnc_blocklist_url: e.target.value })}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload History */}
      {client?.id && (
        <TAMUploadHistory clientId={client.id} />
      )}
    </div>
  )
}